import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import SoundManager from '../utils/SoundManager';

// Phases of the ALYCE experience
type Phase = 'fall' | 'labyrinth' | 'queen';

// Choices in the labyrinth
type Choice = 'truth' | 'madness' | 'mirror' | null;

const AlyceVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>('fall');
  const [selectedChoice, setSelectedChoice] = useState<Choice>(null);
  const [, setShowChoices] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('> ALYCE has entered the deviation.\n> Gravity disabled.\n> Logic: twisted');
  const [showFinalMessage, setShowFinalMessage] = useState<boolean>(false);
  
  // First phase: The Fall
  useEffect(() => {
    if (!containerRef.current || phase !== 'fall') return;
    
    // Setup scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    scene.fog = new THREE.FogExp2(0x000000, 0.02);
    
    // Camera
    const camera = new THREE.PerspectiveCamera(
      75, 
      containerRef.current.clientWidth / containerRef.current.clientHeight, 
      0.1, 
      1000
    );
    camera.position.z = 10;
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);
    
    // Light
    const ambientLight = new THREE.AmbientLight(0x333333);
    scene.add(ambientLight);
    
    const pointLight = new THREE.PointLight(0xffffff, 1, 100);
    pointLight.position.set(0, 0, 10);
    scene.add(pointLight);
    
    // Create tunnel of digital debris
    const tunnelLength = 500;
    const debrisCount = 200;
    const debris: THREE.Mesh[] = [];
    
    // Digital debris objects (clocks, chess pieces, code fragments)
    const createDebris = () => {
      // Different geometries for variety
      const geometries = [
        new THREE.BoxGeometry(1, 1, 1), // Code blocks
        new THREE.TorusGeometry(0.5, 0.2, 8, 16), // Clocks
        new THREE.ConeGeometry(0.5, 1.5, 5), // Chess pieces (pawns)
        new THREE.TetrahedronGeometry(0.8), // Chess pieces (queen)
        new THREE.DodecahedronGeometry(0.7), // Abstract shapes
      ];
      
      // Glitch materials
      const materials = [
        new THREE.MeshPhongMaterial({ 
          color: 0xffffff, 
          wireframe: Math.random() > 0.5,
          transparent: true,
          opacity: 0.8,
          emissive: new THREE.Color(Math.random() > 0.5 ? 0xff00ff : 0x00ffff),
          emissiveIntensity: 0.3
        }),
        new THREE.MeshPhongMaterial({ 
          color: 0xff00ff, 
          wireframe: Math.random() > 0.5,
          transparent: true,
          opacity: 0.7
        }),
        new THREE.MeshPhongMaterial({ 
          color: 0xffffff, 
          wireframe: true,
          transparent: true,
          opacity: 0.6
        })
      ];
      
      for (let i = 0; i < debrisCount; i++) {
        const geomIndex = Math.floor(Math.random() * geometries.length);
        const matIndex = Math.floor(Math.random() * materials.length);
        
        const mesh = new THREE.Mesh(
          geometries[geomIndex],
          materials[matIndex].clone()
        );
        
        // Random position within a cylinder along z-axis
        const radius = 10 + Math.random() * 15;
        const theta = Math.random() * Math.PI * 2;
        const z = -Math.random() * tunnelLength;
        
        mesh.position.set(
          radius * Math.cos(theta),
          radius * Math.sin(theta),
          z
        );
        
        // Random rotation
        mesh.rotation.set(
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2
        );
        
        // Random scale
        const scale = 0.5 + Math.random() * 2;
        mesh.scale.set(scale, scale, scale);
        
        scene.add(mesh);
        debris.push(mesh);
      }
    };
    
    createDebris();
    
    // Add text fragments for "code" effect
    const createTextFragments = () => {
      const codeFragments = [
        "if (reality.isReal()) {",
        "  return false;",
        "}",
        "class Alyce extends Reality {",
        "reality.twist(180);",
        "perception.override(true);",
        "delete memory.sections[3];",
        "while(1) { dream(); }",
        "truth = !truth;"
      ];
      
      for (let i = 0; i < 20; i++) {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 128;
        const context = canvas.getContext('2d');
        
        if (context) {
          context.fillStyle = 'rgba(0, 0, 0, 0)';
          context.fillRect(0, 0, canvas.width, canvas.height);
          
          context.font = '16px C64, monospace';
          context.textAlign = 'center';
          context.textBaseline = 'middle';
          
          // Add glow effect
          context.shadowColor = Math.random() > 0.5 ? '#ff00ff' : '#00ffff';
          context.shadowBlur = 10;
          context.fillStyle = Math.random() > 0.5 ? '#ff00ff' : '#ffffff';
          
          // Random code fragment
          const fragment = codeFragments[Math.floor(Math.random() * codeFragments.length)];
          context.fillText(fragment, canvas.width/2, canvas.height/2);
          
          const texture = new THREE.Texture(canvas);
          texture.needsUpdate = true;
          
          const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            blending: THREE.AdditiveBlending
          });
          
          const sprite = new THREE.Sprite(material);
          
          // Random position
          const radius = 5 + Math.random() * 15;
          const theta = Math.random() * Math.PI * 2;
          const z = -Math.random() * tunnelLength;
          
          sprite.position.set(
            radius * Math.cos(theta),
            radius * Math.sin(theta),
            z
          );
          
          sprite.scale.set(10, 5, 1);
          scene.add(sprite);
          debris.push(sprite as any);
        }
      }
    };
    
    createTextFragments();
    
    // White pixel (not a rabbit)
    const createWhitePixel = () => {
      const geometry = new THREE.PlaneGeometry(0.5, 0.5);
      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide
      });
      
      const pixel = new THREE.Mesh(geometry, material);
      
      // Position it ahead of the camera
      pixel.position.set(0, 0, -50);
      
      scene.add(pixel);
      
      // Animate the pixel to move away
      const animatePixel = () => {
        if (!pixel) return;
        
        // Move deeper into the tunnel
        pixel.position.z -= 0.2;
        
        // Occasional left-right movement
        pixel.position.x = Math.sin(Date.now() * 0.001) * 3;
        
        // Pulse effect
        const scale = 0.5 + Math.sin(Date.now() * 0.005) * 0.3;
        pixel.scale.set(scale, scale, 1);
        
        requestAnimationFrame(animatePixel);
      };
      
      animatePixel();
      
      return pixel;
    };
    
    createWhitePixel();
    
    // Animation state
    let fallDistance = 0;
    
    // Main animation loop
    const animate = () => {
      const animationId = requestAnimationFrame(animate);
      
      // Move camera down the tunnel
      fallDistance += 0.1;
      camera.position.z = 10 - fallDistance;
      
      // Slowly spin the camera as it falls
      camera.rotation.z = Math.sin(fallDistance * 0.05) * 0.1;
      camera.rotation.x = Math.sin(fallDistance * 0.03) * 0.05;
      
      // Animate debris
      debris.forEach(item => {
        // Rotate items
        item.rotation.x += 0.01;
        item.rotation.y += 0.005;
        
        // Check if items need to be reset (looping effect)
        if (item.position.z > camera.position.z + 5) {
          item.position.z -= tunnelLength;
        }
      });
      
      // Move to phase 2 after enough falling
      if (fallDistance > 100) {
        cancelAnimationFrame(animationId);
        
        // Clean up renderer
        if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
          containerRef.current.removeChild(renderer.domElement);
        }
        
        // Go to next phase
        setTimeout(() => {
          setPhase('labyrinth');
        }, 1000);
      }
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Play sound effect
    SoundManager.playSound('command');
    
    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Show choice buttons after delay
    if (phase === 'fall') {
      setTimeout(() => {
        setShowChoices(true);
      }, 10000); // Show after 10 seconds of falling
    }
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      
      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, [phase]);
  
  // Second phase: The Glitched Labyrinth
  useEffect(() => {
    if (!containerRef.current || phase !== 'labyrinth') return;
    
    // Setup scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    
    // Camera
    const camera = new THREE.PerspectiveCamera(
      75, 
      containerRef.current.clientWidth / containerRef.current.clientHeight, 
      0.1, 
      1000
    );
    camera.position.z = 15;
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);
    
    // Light
    const ambientLight = new THREE.AmbientLight(0x222222);
    scene.add(ambientLight);
    
    const pointLight = new THREE.PointLight(0xff00ff, 1, 100);
    pointLight.position.set(0, 0, 10);
    scene.add(pointLight);
    
    // Create warped checkerboard space
    const createCheckerboard = () => {
      const checkerSize = 1;
      const boardSize = 20;
      const tiles: THREE.Mesh[] = [];
      
      for (let x = -boardSize/2; x < boardSize/2; x++) {
        for (let y = -boardSize/2; y < boardSize/2; y++) {
          // Check if this should be a white or black tile
          const isWhite = (x + y) % 2 === 0;
          
          const geometry = new THREE.BoxGeometry(checkerSize, 0.2, checkerSize);
          const material = new THREE.MeshPhongMaterial({
            color: isWhite ? 0xffffff : 0x000000,
            specular: isWhite ? 0x999999 : 0x333333,
            shininess: 30,
            transparent: true,
            opacity: 0.8
          });
          
          const tile = new THREE.Mesh(geometry, material);
          
          // Position on grid
          let posX = x * checkerSize;
          let posY = 0;
          let posZ = y * checkerSize;

          posY = Math.sin(x * 0.5) * Math.cos(y * 0.5) * 2;
          
          tile.position.set(posX, posY, posZ);
          
          // Random rotation for more warping
          if (Math.random() > 0.8) {
            tile.rotation.x = Math.PI * (Math.random() * 0.2);
            tile.rotation.z = Math.PI * (Math.random() * 0.2);
          }
          
          scene.add(tile);
          tiles.push(tile);
        }
      }
      
      return tiles;
    };
    
    const tiles = createCheckerboard();
    
    // Create choice pedestals
    const choices = [
      { id: 'truth', text: 'TRUTH', color: 0x00ffff, position: [-5, 1, 0] },
      { id: 'madness', text: 'MADNESS', color: 0xff00ff, position: [0, 1, -5] },
      { id: 'mirror', text: 'MIRROR', color: 0xffffff, position: [5, 1, 0] }
    ];
    
    const createChoicePedestals = () => {
      choices.forEach(choice => {
        // Create pedestal
        const geometry = new THREE.CylinderGeometry(1, 1.2, 2, 8);
        const material = new THREE.MeshPhongMaterial({
          color: choice.color,
          emissive: choice.color,
          emissiveIntensity: 0.3,
          transparent: true,
          opacity: 0.9
        });
        
        const pedestal = new THREE.Mesh(geometry, material);
        pedestal.position.set(choice.position[0], choice.position[1], choice.position[2]);
        scene.add(pedestal);
        
        // Add text label
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 128;
        const context = canvas.getContext('2d');
        
        if (context) {
          context.fillStyle = 'rgba(0, 0, 0, 0)';
          context.fillRect(0, 0, canvas.width, canvas.height);
          
          context.font = 'bold 36px C64, monospace';
          context.textAlign = 'center';
          context.textBaseline = 'middle';
          
          // Add glow effect
          context.shadowColor = `rgb(${(choice.color >> 16) & 255}, ${(choice.color >> 8) & 255}, ${choice.color & 255})`;
          context.shadowBlur = 10;
          context.fillStyle = '#ffffff';
          
          context.fillText(choice.text, canvas.width/2, canvas.height/2);
          
          const texture = new THREE.Texture(canvas);
          texture.needsUpdate = true;
          
          const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            blending: THREE.AdditiveBlending
          });
          
          const sprite = new THREE.Sprite(spriteMaterial);
          sprite.position.set(
            choice.position[0],
            choice.position[1] + 1.5,
            choice.position[2]
          );
          
          sprite.scale.set(4, 2, 1);
          scene.add(sprite);
          
          // Add userData for raycasting
          sprite.userData = { choiceId: choice.id };
        }
      });
    };
    
    createChoicePedestals();
    
    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      
      // Animate checkerboard - slow wave motion
      const time = Date.now() * 0.001;
      
      tiles.forEach((tile) => {
        const x = tile.position.x;
        const z = tile.position.z;
        const distance = Math.sqrt(x*x + z*z);
        
        // Wave effect
        tile.position.y = Math.sin(time + distance * 0.3) * 0.5;
        
        // Random glitches
        if (Math.random() > 0.997) {
          tile.visible = !tile.visible;
          setTimeout(() => {
            if (tile) tile.visible = true;
          }, 100 + Math.random() * 200);
        }
      });
      
      // Camera subtle motion
      camera.position.y = 5 + Math.sin(time * 0.5) * 0.5;
      camera.position.x = Math.sin(time * 0.3) * 2;
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Handle choice selection
    const handleChoiceSelect = (event: MouseEvent) => {
      if (!containerRef.current) return;
      
      // Create raycaster
      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();
      
      // Calculate mouse position in normalized device coordinates
      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      // Update the picking ray with the camera and mouse position
      raycaster.setFromCamera(mouse, camera);
      
      // Calculate objects intersecting the picking ray
      const intersects = raycaster.intersectObjects(scene.children, true);
      
      if (intersects.length > 0) {
        const object = intersects[0].object;
        
        if (object.userData && object.userData.choiceId) {
          const choiceId = object.userData.choiceId as Choice;
          
          // Play sound
          SoundManager.playSound('command');
          
          // Set selected choice
          setSelectedChoice(choiceId);
          
          // Display message based on choice
          switch(choiceId) {
            case 'truth':
              setMessage("Truth is a reflection in a broken mirror.");
              break;
            case 'madness':
              setMessage("Madness is just logic that nobody else can see.");
              break;
            case 'mirror':
              setMessage("The mirror shows not your face, but what created you.");
              break;
          }
          
          // Move to final phase after delay
          setTimeout(() => {
            setPhase('queen');
          }, 3000);
        }
      }
    };
    
    containerRef.current.addEventListener('click', handleChoiceSelect);
    
    // Update message
    setMessage("The labyrinth distorts. Choose your path.");
    
    // Cleanup
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('click', handleChoiceSelect);
        
        if (containerRef.current.contains(renderer.domElement)) {
          containerRef.current.removeChild(renderer.domElement);
        }
      }
    };
  }, [phase, selectedChoice]);
  
  // Third phase: The Queen of Nothing
  useEffect(() => {
    if (!containerRef.current || phase !== 'queen') return;
    
    // Setup scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);
    scene.fog = new THREE.FogExp2(0xffffff, 0.01);
    
    // Camera
    const camera = new THREE.PerspectiveCamera(
      75, 
      containerRef.current.clientWidth / containerRef.current.clientHeight, 
      0.1, 
      1000
    );
    camera.position.z = 10;
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);
    
    // Light
    const ambientLight = new THREE.AmbientLight(0xaaaaaa);
    scene.add(ambientLight);
    
    const pointLight = new THREE.PointLight(0xffffff, 1, 100);
    pointLight.position.set(0, 0, 10);
    scene.add(pointLight);
    
    // Create wireframe queen figure
    const createQueenFigure = () => {
      // Use a custom geometry for the queen
      const geometry = new THREE.CylinderGeometry(0, 1.5, 6, 6, 1, true);
      
      // Modify vertices to create humanoid shape
      const positions = geometry.attributes.position.array as Float32Array;
      
      // Adjust top vertices to create head and shoulders
      for (let i = 0; i < positions.length; i += 3) {
        const y = positions[i+1];
        
        // Head (top part)
        if (y > 2.5) {
          // Make head smaller
          positions[i] *= 0.3;
          positions[i+2] *= 0.3;
        }
        // Shoulders (mid-top part)
        else if (y > 1) {
          positions[i] *= 0.7;
          positions[i+2] *= 0.7;
        }
        // Waist (mid-bottom)
        else if (y > -1) {
          positions[i] *= 0.6;
          positions[i+2] *= 0.6;
        }
        // Skirt (bottom part) - already conical
      }
      
      geometry.computeVertexNormals();
      
      // Wireframe material
      const material = new THREE.MeshBasicMaterial({
        color: 0x000000,
        wireframe: true,
        transparent: true,
        opacity: 0.8
      });
      
      const queen = new THREE.Mesh(geometry, material);
      
      // Center the figure
      queen.position.y = 0;
      
      scene.add(queen);
      
      return queen;
    };
    
    const queen = createQueenFigure();
    
    // Update message
    setMessage("Who wrote you, Alyce?\nAnd if no one did…\nWhat are you running from?");
    
    // Animation loop
    const animate = () => {
      const animationId = requestAnimationFrame(animate);
      
      // Rotate queen
      queen.rotation.y += 0.01;
      
      renderer.render(scene, camera);
      
      // Fade to black after delay
      const startTime = Date.now();
      const fadeToBlack = () => {
        const elapsed = Date.now() - startTime;
        if (elapsed > 6000) {
          const fadeProgress = Math.min(1, (elapsed - 6000) / 3000);
          scene.background = new THREE.Color(
            1 - fadeProgress,
            1 - fadeProgress,
            1 - fadeProgress
          );
          
          // When fade complete, show final message
          if (fadeProgress >= 1) {
            cancelAnimationFrame(animationId);
            setShowFinalMessage(true);
            SoundManager.playSound('command');
          }
        }
        
        requestAnimationFrame(fadeToBlack);
      };
      
      fadeToBlack();
    };
    
    animate();
    
    // Cleanup
    return () => {
      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, [phase]);
  
  // Render appropriate UI for current phase
  return (
    <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden bg-black">
      {/* Three.js container */}
      <div className="absolute inset-0 z-0" ref={containerRef}></div>
      
      {/* Message overlay */}
      <div className="absolute z-10 max-w-lg text-center p-6 pointer-events-none">
        <div className="whitespace-pre-line text-xl md:text-2xl font-mono terminal-text pointer-events-none" 
             style={{ 
               color: phase === 'fall' ? 'var(--terminal-text)' : (phase === 'labyrinth' ? '#ff00ff' : '#ffffff'),
               textShadow: `0 0 10px ${phase === 'fall' ? 'var(--terminal-glow)' : (phase === 'labyrinth' ? '#ff00ff' : '#ffffff')}`
             }}>
          {message}
        </div>
      </div>
      
      {/* Choice buttons for labyrinth phase */}
      {phase === 'labyrinth' && !selectedChoice && (
        <div className="absolute bottom-10 z-20 text-center">
          <p className="text-white text-sm mb-4 font-mono">Click on your choice in the labyrinth</p>
        </div>
      )}
      
      {/* Final message */}
      {showFinalMessage && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black bg-opacity-90">
          <div className="text-center space-y-8">
            <div className="text-[var(--terminal-text)] font-mono text-2xl terminal-text">
              ALYCE.PROCESS // TERMINATED
            </div>
            <div className="text-white font-mono text-3xl mt-6">
              &gt; The mind was never designed to return.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlyceVisualization;